import { PROGRAM_SPECIFIC_NAMES } from "./constants";
import {
  getAffiliatedCompany,
  getPartnerRegistrationDetails,
} from "../Services/PartnerRegistrationAPI";
import {
  setDxpUserProfileDraft,
  setSelectedRegisterCompanyType,
} from "../Redux/actions";
import { flattenWithSeparator } from "./utils";

const PROGRAM = "DXP";

/** Synced from GET `/partner/:id` personalInfo.id for POST /partner/register. */
const DXP_CONTACT_ID_KEY = "dxpContactId";

/** Set before IMS sign-in when partner GET must run after the user is authenticated. */
export const DXP_PENDING_PARTNER_RESUME_ID_KEY = "dxpPendingPartnerResumeId";

/**
 * Invited contact (API 10019): joins an existing company — user profile only, Register CTA.
 * `redirectTo` is cleared on invite; save-and-exit keeps `redirectTo === "membershipdetails"`.
 */
export function isDxpInvitedContactRegistration(): boolean {
  const accountId = localStorage.getItem("accountId");
  const redirectTo = localStorage.getItem("redirectTo");
  return !!accountId && accountId !== "undefined" && !redirectTo;
}

/** True when saved partner registration should resume at Company information. */
export function shouldResumeDxpRegistrationAtCompanyStep(): boolean {
  const redirectTo = localStorage.getItem("redirectTo");
  const partnerId = localStorage.getItem("accountId");
  return (
    !!partnerId &&
    partnerId !== "undefined" &&
    redirectTo === "membershipdetails"
  );
}

function isValidPartnerId(id: string | null): id is string {
  return !!id && id !== "undefined";
}

/** Silver BC return: `redirectTo=benefitsCenter` or `?redirectTo=membershipdetails`. */
function isBenefitsCenterReturn(): boolean {
  if (localStorage.getItem("redirectTo") === "benefitsCenter") return true;
  if (typeof window === "undefined") return false;
  return (
    new URLSearchParams(window.location.search).get("redirectTo") ===
    "membershipdetails"
  );
}

function sessionHasPersonalInfoId(): boolean {
  try {
    const reg = JSON.parse(
      sessionStorage.getItem("registrationFormData") || "{}",
    ) as Record<string, unknown>;
    const flatId = reg.personalInfo_id;
    if (typeof flatId === "string" && flatId.trim() && flatId !== "undefined") {
      return true;
    }
    const draft = JSON.parse(
      sessionStorage.getItem("dxpUserProfileDraft") || "{}",
    ) as { personalInfo?: { id?: unknown } };
    const nestedId = draft.personalInfo?.id;
    return (
      typeof nestedId === "string" &&
      nestedId.trim() !== "" &&
      nestedId !== "undefined"
    );
  } catch {
    return false;
  }
}

export function getPartnerIdForDxpResumeHydration(): string | null {
  const pending = localStorage.getItem(DXP_PENDING_PARTNER_RESUME_ID_KEY);
  if (isValidPartnerId(pending)) return pending;
  if (!shouldResumeDxpRegistrationAtCompanyStep()) return null;
  return localStorage.getItem("accountId");
}

/** Defers API hydration until after IMS redirect (no auth token before sign-in). */
export function markDxpPartnerResumePendingAfterIms(partnerId: string): void {
  localStorage.setItem(DXP_PENDING_PARTNER_RESUME_ID_KEY, partnerId);
  localStorage.setItem("partnerRegistrationFormStep", "4");
}

export function clearDxpPartnerResumePending(): void {
  localStorage.removeItem(DXP_PENDING_PARTNER_RESUME_ID_KEY);
}

/** Positions invited users on User profile; hides company/membership wizard steps. */
export function prepareDxpInvitedContactWizardState(): void {
  localStorage.setItem("partnerRegistrationFormStep", "3");
  localStorage.removeItem("selectedRegisterCompanyType");
  clearDxpPartnerResumePending();
}

/** Syncs hydrated session/local storage into Redux after partner GET succeeds. */
export function syncResumeHydrationToRedux(
  dispatch: (action: ReturnType<typeof setSelectedRegisterCompanyType>) => void,
): void {
  const companyType = localStorage.getItem("selectedRegisterCompanyType");
  if (companyType) {
    dispatch(setSelectedRegisterCompanyType(companyType));
  }
  const draftRaw = sessionStorage.getItem("dxpUserProfileDraft");
  if (draftRaw) {
    try {
      dispatch(setDxpUserProfileDraft(JSON.parse(draftRaw)));
    } catch {
      dispatch(setDxpUserProfileDraft(null));
    }
  }
}

/** API → UI label for company step pickers (inverse of save payload mapping). */
const REVERSE_PRIMARY_BUSINESS: Record<string, string> = {
  Solution: "Consulting/Professional Services",
  Technology: "ISV/Data Provider",
};

/**
 * Maps GET `/api/v1/DXP/partner/:id` JSON into the flattened `registrationFormData`
 * shape consumed by `CompanyInformation` / `MembershipOptions`.
 */
export function buildSessionPayloadFromPartnerDetailsApi(
  data: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  if (!data || typeof data !== "object") return {};

  const rawCompany = data.companyInfo as Record<string, unknown> | undefined;
  const companyInfo = rawCompany ? { ...rawCompany } : {};

  const address = companyInfo.address as Record<string, unknown> | undefined;
  if (
    companyInfo.addressChoice == null &&
    (address?.addressChoice === "Parent Address" ||
      address?.addressChoice === "Partner Address")
  ) {
    companyInfo.addressChoice = address.addressChoice;
  }

  if (typeof companyInfo.isSubsidiary === "boolean") {
    companyInfo.isSubsidiary = companyInfo.isSubsidiary ? "Yes" : "No";
  }

  const pb = companyInfo.primaryBusiness;
  if (typeof pb === "string" && REVERSE_PRIMARY_BUSINESS[pb]) {
    companyInfo.primaryBusiness = REVERSE_PRIMARY_BUSINESS[pb];
  }

  const personalInfo =
    data.personalInfo && typeof data.personalInfo === "object"
      ? { ...(data.personalInfo as Record<string, unknown>) }
      : {};

  const flat = flattenWithSeparator(
    { companyInfo, personalInfo },
    "_",
  ) as Record<string, unknown>;

  const isSubsidiaryFlat = flat.companyInfo_isSubsidiary === "Yes";
  if (
    isSubsidiaryFlat &&
    flat.companyInfo_addressChoice == null &&
    (String(flat.companyInfo_address_hqAddress ?? "").trim() ||
      String(flat.companyInfo_address_country ?? "").trim() ||
      String(flat.companyInfo_address_city ?? "").trim() ||
      String(flat.companyInfo_address_zipCode ?? "").trim())
  ) {
    flat.companyInfo_addressChoice = "Partner Address";
  }

  const affiliated = rawCompany?.affiliatedCompany as
    | { name?: string }
    | undefined;
  if (affiliated?.name) {
    flat.affiliatedCompanyName = affiliated.name;
  }

  const iso =
    (typeof companyInfo.phoneCountryCode === "string"
      ? companyInfo.phoneCountryCode
      : "") ||
    (typeof (personalInfo as { phoneCountryCode?: string }).phoneCountryCode ===
    "string"
      ? (personalInfo as { phoneCountryCode: string }).phoneCountryCode
      : "");
  const localPhone = String(companyInfo.phone ?? "").trim();
  const existingPhone = String(flat.companyInfo_phone ?? "").trim();
  if (iso && localPhone && !existingPhone.includes("-")) {
    flat.companyInfo_phone = `${iso}-${localPhone}`;
  }

  return flat;
}

/** Resolves standalone vs subsidiary company type from partner GET payload. */
export function resolveCompanyTypeFromPartnerApiData(
  apiData: Record<string, unknown>,
): "subsidaryCompany" | "standaloneCompany" {
  const companyInfo = apiData.companyInfo as
    | { isSubsidiary?: boolean | string }
    | undefined;
  const isSub =
    companyInfo?.isSubsidiary === true || companyInfo?.isSubsidiary === "Yes";
  return isSub ? "subsidaryCompany" : "standaloneCompany";
}

async function hydrateMissingParentAddress(
  flat: Record<string, unknown>,
): Promise<void> {
  const parentName = String(
    flat.companyInfo_affiliatedCompany_name ?? "",
  ).trim();
  const hasSavedAddress = Boolean(
    String(flat.companyInfo_address_hqAddress ?? "").trim() ||
    String(flat.companyInfo_address_country ?? "").trim(),
  );
  if (
    flat.companyInfo_isSubsidiary !== "Yes" ||
    !parentName ||
    (flat.companyInfo_addressChoice === "Parent Address" && hasSavedAddress)
  ) {
    return;
  }
  const result = (await getAffiliatedCompany(
    PROGRAM_SPECIFIC_NAMES[PROGRAM].programType,
    parentName,
  ).catch(() => null)) as {
    status?: number;
    data?: {
      accountId?: string;
      id?: string;
      name?: string;
      level?: string;
      address?: Record<string, unknown>;
    }[];
  } | null;
  if (result?.status !== 1 || !Array.isArray(result.data)) return;
  const parentId = String(
    flat.companyInfo_affiliatedCompany_accountId ?? "",
  ).trim();
  const matches = result.data.filter((company) =>
    parentId
      ? company.accountId === parentId || company.id === parentId
      : company.name === parentName,
  );
  if (matches.length !== 1 || !matches[0].address) return;
  const matchedParent = matches[0];
  const matchedId = matchedParent.accountId ?? matchedParent.id;
  if (!parentId && matchedId) {
    flat.companyInfo_affiliatedCompany_accountId = matchedId;
  }
  if (matchedParent.level != null) {
    flat.companyInfo_affiliatedCompany_level = matchedParent.level;
  }
  const parentAddress = matches[0].address;
  [
    "hqAddress",
    "country",
    "countryName",
    "state",
    "stateName",
    "city",
    "zipCode",
  ].forEach((key) => {
    if (parentAddress[key] != null) {
      flat[`companyInfo_affiliatedCompany_${key}`] = parentAddress[key];
    }
  });
}

/**
 * Loads saved partner registration from the API into session/local storage and
 * positions the wizard at Company information (step 4). Used when resuming after
 * save-and-exit or continue-registration (code 10018).
 */
export async function persistDxpRegistrationResumeFromPartnerApi(
  partnerId: string,
): Promise<boolean> {
  const result: { status?: number; data?: Record<string, unknown> } =
    await getPartnerRegistrationDetails(
      PROGRAM_SPECIFIC_NAMES[PROGRAM].programType,
      partnerId,
    );
  if (result?.status !== 1 || !result?.data) return false;

  const apiData = result.data;
  const companyType = resolveCompanyTypeFromPartnerApiData(apiData);

  localStorage.setItem("selectedRegisterCompanyType", companyType);
  const flat = buildSessionPayloadFromPartnerDetailsApi(apiData);
  await hydrateMissingParentAddress(flat);
  sessionStorage.setItem("registrationFormData", JSON.stringify(flat));

  const draftPi = apiData.personalInfo;
  if (draftPi && typeof draftPi === "object") {
    sessionStorage.setItem(
      "dxpUserProfileDraft",
      JSON.stringify({ personalInfo: draftPi }),
    );
    const personalId = (draftPi as { id?: unknown }).id;
    if (typeof personalId === "string" && personalId.trim()) {
      localStorage.setItem(DXP_CONTACT_ID_KEY, personalId.trim());
    }
  } else {
    sessionStorage.removeItem("dxpUserProfileDraft");
  }

  if (typeof window !== "undefined") {
    const params = new URLSearchParams(window.location.search);
    if (params.get("redirectTo") === "membershipdetails") {
      params.delete("redirectTo");
      const query = params.toString();
      const next = `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`;
      window.history.replaceState(null, "", next);
    }
  }

  localStorage.setItem(
    "partnerRegistrationFormStep",
    String(isBenefitsCenterReturn() ? 5 : 4),
  );
  return true;
}

export function shouldHydrateDxpBenefitsCenterReturn(): boolean {
  const partnerId = localStorage.getItem("accountId");
  return (
    isValidPartnerId(partnerId) &&
    !sessionHasPersonalInfoId() &&
    isBenefitsCenterReturn()
  );
}

/** 2nd checkout after Silver BC: GET `/partner/:id` for `personalInfo.id`. */
export async function hydrateDxpBenefitsCenterReturnIfNeeded(): Promise<boolean> {
  if (!shouldHydrateDxpBenefitsCenterReturn()) return false;
  return persistDxpRegistrationResumeFromPartnerApi(
    localStorage.getItem("accountId") as string,
  );
}

/** Loads partner draft from API when a resume is pending and the user is signed in. */
export async function hydrateDxpRegistrationResumeIfNeeded(): Promise<boolean> {
  const partnerId = getPartnerIdForDxpResumeHydration();
  if (!isValidPartnerId(partnerId) || sessionHasPersonalInfoId()) return false;
  const ok = await persistDxpRegistrationResumeFromPartnerApi(partnerId);
  if (ok) clearDxpPartnerResumePending();
  return ok;
}
